import * as React from 'react';
import { styled, useTheme, Theme, CSSObject } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import CssBaseline from '@mui/material/CssBaseline';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import PeopleIcon from '@mui/icons-material/People';
import BusinessIcon from '@mui/icons-material/Business';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import GavelIcon from '@mui/icons-material/Gavel';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import SignOutSvg from 'src/components/atoms/svg/SignOutSvg';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { useAppDispatch, useAppSelector } from 'src/app/reduxCustomHooks';
import { logoutAction } from 'src/app/slices/authSlice';
import { api } from 'src/app/services/api';
import { selectedItemDefiner } from './ListItems';
import { sidebarItemType } from 'src/types/listItemsTypes';
import { SvgIconProps } from '@mui/material/SvgIcon';
import HomeIcon from '@mui/icons-material/Home';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import { useIsDemoUser } from 'src/components/hooks/useIsDemoUser';
import { useSsoStatus } from 'src/components/hooks/useSsoStatus';
import { ThemeModeContext } from 'src/components/context/ThemeModeContext';
import { useUsersControllerGetUserProfileQuery } from 'src/app/services/api.generated';
import { Divider, Tooltip, Typography, useMediaQuery } from '@mui/material';

const drawerWidth = 240;

/** Shared look of every sidebar row: compact, quiet hover, accent bar when active. */
const navItemSx = {
  minHeight: 40,
  mx: 1,
  my: 0.25,
  px: 1.5,
  backgroundColor: 'transparent',
  position: 'relative',
  color: 'text.secondary',
  '&:hover': { color: 'text.primary' },
  '&.Mui-selected': { color: 'text.primary' },
  '&.Mui-selected::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: 8,
    bottom: 8,
    width: 3,
    borderRadius: 3,
    bgcolor: 'primary.main',
  },
  '&.Mui-focusVisible': {
    outline: '2px solid',
    outlineColor: 'primary.main',
    outlineOffset: -2,
  },
} as const;

/** Hairline between the nav groups and the footer, inset like the group dividers. */
const footerSeparatorSx = {
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 16,
    right: 16,
    borderTop: 1,
    borderColor: 'divider',
  },
} as const;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.easeOut,
    duration: theme.transitions.duration.short,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  // Closing is high-frequency; snap width to avoid sluggish feel (Emil: frequency rule)
  transition: 'none',
  overflowX: 'hidden',
  width: `calc(${theme.spacing(11)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(11)} + 1px)`,
  },
});


const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  padding: theme.spacing(1, 2.5),
  minHeight: '56px',
  position: 'relative',
  overflow: 'hidden',
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
  }),
);

type SidebarPropsType = {
  openSandwichMenu: boolean;
  setOpenSandwichMenu: React.Dispatch<React.SetStateAction<boolean>>;
  defaultOpen?: boolean;
};

export const Sidebar: React.FC<SidebarPropsType> = ({
  openSandwichMenu,
  setOpenSandwichMenu,
  defaultOpen = false,
}) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'));
  const [open, setOpen] = React.useState(defaultOpen);
  const isDemoUser = useIsDemoUser();
  const { mode, toggleMode } = React.useContext(ThemeModeContext);

  const handleDrawerOpen = () => {
    setOpen(true);
    setOpenSandwichMenu(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
    setOpenSandwichMenu(false);
  };

  const closeMobileMenu = () => {
    if (!isMd) {
      setOpenSandwichMenu(false);
    }
  };

  React.useEffect(() => {
    closeMobileMenu();
    // Only on route or breakpoint change; closeMobileMenu is re-created per render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, isMd]);

  React.useEffect(() => {
    if (!isMd) {
      setOpen(false);
    }
  }, [isMd]);

  const { isAdmin, isManager, token, username } = useAppSelector((state) => ({
    isAdmin: state.auth?.isAdmin,
    isManager: state.auth?.user?.isManager,
    token: state.auth?.token,
    username: state.auth?.user?.username,
  }));
  const { data: profile } = useUsersControllerGetUserProfileQuery(undefined, {
    skip: !token || isDemoUser,
  });
  const isOrgOwner =
    !!profile && 'company' in profile && profile.company?.owner === username;
  const sso = useSsoStatus();
  const canSeeSsoSettings =
    sso.enabled && !isDemoUser && (isOrgOwner || !!isAdmin);
  // Case system (ZGW) settings: company owner or global admin, no flag.
  const canSeeZgwSettings = !isDemoUser && (isOrgOwner || !!isAdmin);
  const canSeeAudit = !isDemoUser && (!!isManager || isOrgOwner);

  const logoutHandler = () => {
    closeMobileMenu();
    dispatch(logoutAction());
    dispatch(api.util.resetApiState());
    navigate('/login');
  };

  const navigateTo = (path: string) => {
    navigate(path);
    closeMobileMenu();
  };

  const selectedIconRenderHelper: (path: string) => SvgIconProps = (path) => {
    const isSelected = selectedItemDefiner(pathname, path);

    return {
      color: isSelected ? 'primary' : 'action',
      // Static color cue only; no glow animation (high-frequency nav)
      sx: {
        opacity: isSelected ? 1 : 0.72,
      },
    };
  };

  const allItems: (sidebarItemType & {
    group: 'work' | 'library' | 'manage';
  })[] = [
    {
      title: t('dashboard'),
      path: '/',
      group: 'work',
      icon: <HomeIcon {...selectedIconRenderHelper('/')} />,
      disabled: isDemoUser,
    },
    {
      title: t('workspaces'),
      path: '/workspaces',
      group: 'work',
      icon: <WorkspacesIcon {...selectedIconRenderHelper('/workspaces')} />,
    },
    {
      title: t('users'),
      path: '/users',
      group: 'manage',
      icon: <PeopleIcon {...selectedIconRenderHelper('/users')} />,
      invisible: isDemoUser || (!isAdmin && !isManager),
    },
    {
      title: t('companies'),
      path: '/companies',
      group: 'manage',
      icon: <BusinessIcon {...selectedIconRenderHelper('/companies')} />,
      invisible: isDemoUser || !isAdmin,
    },
    {
      title: t('dictionary'),
      path: '/dictionary',
      group: 'library',
      icon: <LibraryBooksIcon {...selectedIconRenderHelper('/dictionary')} />,
      disabled: isDemoUser,
    },
    {
      title: t('legalCodes.title'),
      path: '/legal-codes',
      group: 'library',
      icon: <GavelIcon {...selectedIconRenderHelper('/legal-codes')} />,
      invisible: isDemoUser || (!isAdmin && !isManager),
    },
    {
      title: t('archive'),
      path: '/archive',
      group: 'work',
      icon: (
        <Inventory2OutlinedIcon {...selectedIconRenderHelper('/archive')} />
      ),
      invisible: isDemoUser,
    },
    {
      title: t('trash'),
      path: '/trash',
      group: 'work',
      icon: <DeleteOutlineIcon {...selectedIconRenderHelper('/trash')} />,
      disabled: isDemoUser,
    },
    {
      title: t('sso.settings.title'),
      path: '/sso-settings',
      group: 'manage',
      icon: <VpnKeyOutlinedIcon {...selectedIconRenderHelper('/sso-settings')} />,
      invisible: !canSeeSsoSettings,
    },
    {
      title: t('zgw.settings.title'),
      path: '/zgw-settings',
      group: 'manage',
      icon: <LinkOutlinedIcon {...selectedIconRenderHelper('/zgw-settings')} />,
      invisible: !canSeeZgwSettings,
    },
    {
      title: t('audit'),
      path: '/audit',
      group: 'manage',
      icon: <FactCheckOutlinedIcon {...selectedIconRenderHelper('/audit')} />,
      invisible: !canSeeAudit,
    },
  ];
  const profileItem: sidebarItemType = {
    title: t('profile'),
    path: '/profile',
    icon: <AccountCircleIcon {...selectedIconRenderHelper('/profile')} />,
    disabled: isDemoUser,
  };
  const groupDefs = [
    { key: 'work', label: t('navGroupWork') },
    { key: 'library', label: t('navGroupLibrary') },
    { key: 'manage', label: t('navGroupManage') },
  ] as const;
  const registeredUsersOnlyTooltip = t('registeredUsersOnly');
  // The label names the theme a click switches to, like the icon.
  const themeToggleLabel = mode === 'dark' ? t('themeLight') : t('themeDark');
  const themeToggleAriaLabel =
    mode === 'dark' ? t('switchToLightTheme') : t('switchToDarkTheme');
  const isDrawerExpanded = isMd ? open : true;

  const renderNavItem = (item: sidebarItemType) => (
    <ListItem key={item.title} disablePadding>
      <Tooltip
        title={item.disabled ? registeredUsersOnlyTooltip : item.title}
        placement="right"
        enterDelay={400}
        enterNextDelay={0}
        disableHoverListener={!isMd || (isDrawerExpanded && !item.disabled)}
      >
        <span style={{ display: 'block', width: '100%' }}>
          <ListItemButton
            disabled={item.disabled}
            selected={selectedItemDefiner(pathname, item.path)}
            sx={{ ...navItemSx, justifyContent: 'flex-start' }}
            onClick={() => navigateTo(item.path)}
          >
            <ListItemIcon
              sx={{
                minWidth: '40px',
                display: 'flex',
                justifyContent: 'center',
                zIndex: 2,
              }}
            >
              {React.cloneElement(item.icon, {
                style: {
                  fontSize: '1.35rem',
                  position: 'relative',
                },
              })}
            </ListItemIcon>
            <ListItemText
              primary={item.title}
              primaryTypographyProps={{
                fontSize: '0.9375rem',
                fontWeight: selectedItemDefiner(pathname, item.path)
                  ? 600
                  : 400,
              }}
              sx={{
                opacity: isDrawerExpanded ? 1 : 0,
                visibility: isDrawerExpanded ? 'visible' : 'hidden',
                position: isDrawerExpanded ? 'static' : 'absolute',
                left: isDrawerExpanded ? undefined : '72px',
                transition: isDrawerExpanded
                  ? 'opacity 150ms cubic-bezier(0.2, 0, 0, 1)'
                  : 'none',
              }}
            />
          </ListItemButton>
        </span>
      </Tooltip>
    </ListItem>
  );

  const drawerContent = (
    <>
      <DrawerHeader>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            width: '100%',
            position: 'relative',
          }}
          onClick={() => navigateTo(isDemoUser ? '/workspaces' : '/')}
        >
          <img
            src={
              process.env.PUBLIC_URL +
              (theme.palette.mode === 'light'
                ? '/icons/svg/logo-blue.svg'
                : '/icons/png/logo.png')
            }
            alt="Anonify Logo"
            style={{
              width: isDrawerExpanded ? '120px' : '48px',
              position: 'absolute',
              left: isDrawerExpanded ? '0' : '50%',
              transform: isDrawerExpanded ? 'none' : 'translateX(-50%)',
              marginLeft: isDrawerExpanded ? '7px' : 0,
              transitionProperty: 'width, left, transform, margin-left',
              transitionDuration: '150ms',
              transitionTimingFunction: 'cubic-bezier(0.2, 0, 0, 1)',
            }}
          />
        </Box>
      </DrawerHeader>
      <List
        sx={{
          flexGrow: 1,
          backgroundColor: 'customGrey.light',
          px: 0.5,
          pt: 1,
        }}
      >
        {groupDefs.map((group, index) => {
          const items = allItems.filter(
            (item) => item.group === group.key && !item.invisible,
          );
          if (items.length === 0) return null;

          return (
            <Box
              component="li"
              key={group.key}
              role="presentation"
              sx={{ listStyle: 'none' }}
            >
              {isDrawerExpanded ? (
                <Typography
                  variant="caption"
                  component="div"
                  sx={{
                    px: 3,
                    pt: index === 0 ? 0.5 : 2.5,
                    pb: 0.75,
                    // text.secondary: >= 4.5:1 on the sidebar in both themes (WCAG 1.4.3).
                    color: 'text.secondary',
                    fontSize: '0.6875rem',
                    fontWeight: 600,
                    lineHeight: 1.5,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                  }}
                >
                  {group.label}
                </Typography>
              ) : (
                index > 0 && <Divider sx={{ mx: 2, my: 1 }} />
              )}
              <List disablePadding aria-label={group.label}>
                {items.map(renderNavItem)}
              </List>
            </Box>
          );
        })}
      </List>

      {isMd && (
        <List
          sx={{
            backgroundColor: 'customGrey.light',
            px: 0.5,
            ...footerSeparatorSx,
          }}
        >
          <ListItem disablePadding>
            <ListItemButton
              onClick={open ? handleDrawerClose : handleDrawerOpen}
              aria-label={open ? 'Collapse sidebar' : 'Expand sidebar'}
              sx={navItemSx}
            >
              <ListItemIcon
                sx={{
                  minWidth: '40px',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                {open ? (
                  <MenuOpenIcon sx={{ fontSize: '1.35rem' }} />
                ) : (
                  <MenuIcon sx={{ fontSize: '1.35rem' }} />
                )}
              </ListItemIcon>
            </ListItemButton>
          </ListItem>
        </List>
      )}

      <Box
        component="ul"
        sx={{
          listStyle: 'none',
          m: 0,
          backgroundColor: 'customGrey.light',
          px: 0.5,
          pt: isMd ? 0 : 1,
          pb: 1,
          position: 'relative',
          ...(!isMd && footerSeparatorSx),
        }}
      >
        <ListItem key="themeMode" disablePadding sx={{ display: 'block' }}>
          <Tooltip
            title={themeToggleLabel}
            placement="right"
            enterDelay={400}
            enterNextDelay={0}
            disableHoverListener={!isMd || isDrawerExpanded}
          >
            <ListItemButton
              sx={navItemSx}
              onClick={toggleMode}
              aria-label={themeToggleAriaLabel}
              data-testid="theme-mode-toggle"
            >
              <ListItemIcon
                sx={{
                  minWidth: '40px',
                  display: 'flex',
                  justifyContent: 'center',
                  zIndex: 2,
                }}
              >
                {mode === 'dark' ? (
                  <LightModeOutlinedIcon sx={{ fontSize: '1.35rem' }} />
                ) : (
                  <DarkModeOutlinedIcon sx={{ fontSize: '1.35rem' }} />
                )}
              </ListItemIcon>
              <ListItemText
                primary={themeToggleLabel}
                primaryTypographyProps={{ fontSize: '0.9375rem' }}
                sx={{
                  opacity: isDrawerExpanded ? 1 : 0,
                  visibility: isDrawerExpanded ? 'visible' : 'hidden',
                  position: isDrawerExpanded ? 'static' : 'absolute',
                  left: isDrawerExpanded ? undefined : '72px',
                  transition: isDrawerExpanded
                    ? 'opacity 150ms cubic-bezier(0.2, 0, 0, 1)'
                    : 'none',
                }}
              />
            </ListItemButton>
          </Tooltip>
        </ListItem>
        {renderNavItem(profileItem)}
        {!isDemoUser && (
          <ListItem key="signOut" disablePadding sx={{ display: 'block' }}>
            <ListItemButton
              sx={navItemSx}
              onClick={logoutHandler}
            >
              <ListItemIcon
                sx={{
                  minWidth: '40px',
                  display: 'flex',
                  justifyContent: 'center',
                  zIndex: 2,
                }}
              >
                <SignOutSvg style={{ fontSize: '1.35rem' }} />
              </ListItemIcon>
              <ListItemText
                primary={t('signOut')}
                primaryTypographyProps={{ fontSize: '0.9375rem' }}
                sx={{
                  opacity: isDrawerExpanded ? 1 : 0,
                  visibility: isDrawerExpanded ? 'visible' : 'hidden',
                  position: isDrawerExpanded ? 'static' : 'absolute',
                  left: isDrawerExpanded ? undefined : '72px',
                  transition: isDrawerExpanded
                    ? 'opacity 150ms cubic-bezier(0.2, 0, 0, 1)'
                    : 'none',
                }}
              />
            </ListItemButton>
          </ListItem>
        )}
      </Box>
    </>
  );

  const drawerPaperSx = {
    boxSizing: 'border-box',
    backgroundColor: 'customGrey.light',
  };

  return (
    <Box component="nav" sx={{ flexShrink: 0, width: { xs: 0, md: 'auto' } }}>
      <CssBaseline />
      <MuiDrawer
        variant="temporary"
        open={openSandwichMenu}
        onClose={closeMobileMenu}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            ...drawerPaperSx,
            width: drawerWidth,
          },
        }}
      >
        {drawerContent}
      </MuiDrawer>
      <Drawer
        variant="permanent"
        open={open}
        sx={{
          display: { xs: 'none', md: 'block' },
        }}
      >
        {drawerContent}
      </Drawer>
    </Box>
  );
};
